import React, { useState, useEffect, useRef } from 'react';
import api from '../../api/client';
import './FertRawMaterialPopup.css';
import './BrandPopup.css';

const emptyRow = () => ({ brandID: '', brandName: '' });

const BrandPopup = ({ isOpen, onClose, onSaved, editData = null }) => {
    const [rows,   setRows]   = useState([emptyRow()]);
    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');
    const [rowErrors, setRowErrors] = useState({}); // {index: msg}

    const firstInputRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        if (editData) {
            setRows([{ brandID: editData.brandID || '', brandName: editData.brandName || '' }]);
        } else {
            setRows([emptyRow()]);
        }
        setError(''); setRowErrors({});
        setTimeout(() => firstInputRef.current?.focus(), 60);
    }, [isOpen, editData]);

    if (!isOpen) return null;

    const updateRow = (i, field, val) =>
        setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
    const addRow    = () => setRows(prev => [...prev, emptyRow()]);
    const removeRow = (i) => setRows(prev => prev.filter((_, idx) => idx !== i));

    const handleKeyDown = (e, i, field) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (editData) { handleSave(); return; }
            // Tab-style: ID → Name → next row's ID
            if (field === 'brandID') {
                // move to name field of same row
                e.currentTarget.closest('.brand-row')?.querySelector('.brand-input--name')?.focus();
            } else if (i === rows.length - 1) {
                setRows(prev => [...prev, emptyRow()]);
                setTimeout(() => {
                    const allID = document.querySelectorAll('.brand-input--id');
                    allID[allID.length - 1]?.focus();
                }, 30);
            }
        }
    };

    const handleSave = async () => {
        setError(''); setRowErrors({});
        if (editData) {
            const name = rows[0].brandName.trim();
            if (!name) { setError('Brand Name is required'); return; }
            setSaving(true);
            try {
                await api.put(`/api/brands-batch/${editData.brandID}`, { brandName: name });
                onSaved(); onClose();
            } catch (err) {
                setError(err.response?.data?.error || 'Save failed');
            } finally { setSaving(false); }
            return;
        }

        // Add mode
        const valid = rows.filter(r => r.brandID.trim() && r.brandName.trim());
        if (valid.length === 0) {
            setError('Each row needs both a Brand ID and Brand Name');
            return;
        }
        // Validate ID format
        const newRowErrors = {};
        rows.forEach((r, i) => {
            if ((r.brandID.trim() || r.brandName.trim()) && !r.brandID.trim())
                newRowErrors[i] = 'Brand ID required';
            else if ((r.brandID.trim() || r.brandName.trim()) && !r.brandName.trim())
                newRowErrors[i] = 'Brand Name required';
        });
        if (Object.keys(newRowErrors).length) { setRowErrors(newRowErrors); return; }

        setSaving(true);
        try {
            const res = await api.post(`/api/brands-batch`, {
                brands: valid.map(r => ({ brandID: r.brandID.trim(), brandName: r.brandName.trim() })),
            });
            if (res.data.errors?.length) {
                setError(res.data.errors.join(' · '));
                if (res.data.inserted.length > 0) { onSaved(); }
            } else {
                onSaved(); onClose();
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Save failed');
        } finally { setSaving(false); }
    };

    const filledCount = rows.filter(r => r.brandID.trim() && r.brandName.trim()).length;

    return (
        <div className="frm-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="frm-popup brand-popup">

                {/* Header */}
                <div className="frm-header">
                    <div>
                        <div className="frm-title">
                            {editData ? `Edit Brand — ${editData.brandID}` : 'Add Brands'}
                        </div>
                        <div className="frm-subtitle">
                            {editData
                                ? 'Brand ID cannot be changed'
                                : 'Enter Brand ID (short code) and Brand Name for each brand'}
                        </div>
                    </div>
                    <button className="frm-close" onClick={onClose}><i className="fas fa-times" /></button>
                </div>

                {/* Body */}
                <div className="frm-body">

                    {/* Column headers — add mode only */}
                    {!editData && (
                        <div className="brand-col-headers">
                            <span className="brand-col-headers__id">Brand ID</span>
                            <span className="brand-col-headers__name">Brand Name</span>
                        </div>
                    )}

                    <div className="frm-rows">
                        {rows.map((row, i) => (
                            <div key={i}>
                                <div className={`frm-row brand-row${rowErrors[i] ? ' brand-row--error' : ''}`}>
                                    {/* Index / icon */}
                                    <div className="frm-row__num">
                                        {editData
                                            ? <i className="fas fa-tag frm-icon" />
                                            : <span className="frm-row__index">{i + 1}</span>
                                        }
                                    </div>

                                    {/* Brand ID */}
                                    <input
                                        ref={i === 0 ? firstInputRef : null}
                                        className="frm-input brand-input--id"
                                        value={row.brandID}
                                        readOnly={!!editData}
                                        onChange={e => updateRow(i, 'brandID', e.target.value.toUpperCase())}
                                        onKeyDown={e => handleKeyDown(e, i, 'brandID')}
                                        placeholder="OR"
                                        maxLength={10}
                                        style={editData ? { background: '#F8FAFC', color: '#64748B', cursor: 'default', fontWeight: 700 } : {}}
                                    />

                                    {/* Brand Name */}
                                    <input
                                        className="frm-input brand-input--name"
                                        value={row.brandName}
                                        onChange={e => updateRow(i, 'brandName', e.target.value)}
                                        onKeyDown={e => handleKeyDown(e, i, 'brandName')}
                                        placeholder="e.g. Organo Republic"
                                    />

                                    {/* Remove — add mode, more than 1 row */}
                                    {!editData && rows.length > 1 && (
                                        <button className="frm-remove" onClick={() => removeRow(i)} title="Remove">
                                            <i className="fas fa-times" />
                                        </button>
                                    )}
                                </div>
                                {rowErrors[i] && (
                                    <div className="brand-row-error">{rowErrors[i]}</div>
                                )}
                            </div>
                        ))}
                    </div>

                    {!editData && (
                        <button className="frm-add-row" onClick={addRow}>
                            <i className="fas fa-plus" />
                            Add another
                        </button>
                    )}

                    {error && <div className="frm-error"><i className="fas fa-exclamation-triangle" /> {error}</div>}
                </div>

                {/* Footer */}
                <div className="frm-footer">
                    <button className="frm-btn frm-btn--ghost" onClick={onClose}>Cancel</button>
                    <button className="frm-btn frm-btn--primary" onClick={handleSave} disabled={saving}>
                        {saving
                            ? <><i className="fas fa-spinner fa-spin" /> Saving…</>
                            : editData
                                ? <><i className="fas fa-check" /> Save</>
                                : <><i className="fas fa-plus" /> Add {filledCount > 1 ? `${filledCount} brands` : 'brand'}</>
                        }
                    </button>
                </div>

            </div>
        </div>
    );
};

export default BrandPopup;
